import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { messages, type Lang, type MessageKey } from './messages';

const STORAGE_KEY = 'cs2erp.lang';

/** Objetos do backend que já vêm com as duas línguas. */
export interface Localized {
  pt?: string | null;
  en?: string | null;
}

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Texto traduzido, com interpolação de `{chave}`. */
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
  /** Escolhe o campo certo de um objeto {pt, en} vindo da API. */
  loc: (value: Localized | null | undefined, fallback?: string) => string;
  /** Locale para Intl (datas e números). */
  locale: string;
}

const I18nContext = createContext<I18nValue | null>(null);

function detectLang(): Lang {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored === 'pt' || stored === 'en') return stored;
  const nav = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : 'pt-br';
  return nav.startsWith('pt') ? 'pt' : 'en';
}

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang === 'pt' ? 'pt-BR' : 'en';
  }, [lang]);

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) => {
      const entry = messages[key];
      // Sem tradução, a chave crua é melhor que uma tela em branco.
      if (!entry) return key;
      return interpolate(entry[lang] ?? entry.pt, vars);
    },
    [lang],
  );

  const loc = useCallback(
    (value: Localized | null | undefined, fallback = '—') => {
      if (!value) return fallback;
      return (lang === 'pt' ? value.pt : value.en) ?? value.pt ?? value.en ?? fallback;
    },
    [lang],
  );

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang: setLangState,
      t,
      loc,
      locale: lang === 'pt' ? 'pt-BR' : 'en-US',
    }),
    [lang, t, loc],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n precisa estar dentro de <I18nProvider>');
  return ctx;
}
